import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { ProductLogisticsFields, logisticsFromProduct, logisticsPayload } from '@/components/ProductLogisticsFields';
import { ProductShippingType, shippingFromProduct, shippingPayload } from '@/components/ProductShippingType';
import { PackageDataNotice } from '@/components/PackageDataNotice';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { formatCOP } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';
import { Trash2, Upload, Plus, Search, Pencil, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { SELLER } from '@/constants/testIds';
import { ImageUploader, resolveImageUrl } from '@/components/ImageUploader';
import { startWompiPayment, getPaymentsConfig } from '@/lib/wompi';
import { useAuth } from '@/lib/auth';

const EMPTY = {
  name: '', category: '', brand: '', reference: '',
  description: '', price: '', stock: '',
  vehicle_compat: '', images: [], status: 'active',
  condition: 'generico', warranty_months: '', delivery_days: '', free_shipping: false,
  ...shippingFromProduct(null),
  ...logisticsFromProduct(null),
};

export default function SellerProducts() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [q, setQ] = useState('');
  const [sponsorProduct, setSponsorProduct] = useState(null);
  const [sponsorDays, setSponsorDays] = useState(7);
  const [sponsorLoading, setSponsorLoading] = useState(false);
  const [sponsorTiers, setSponsorTiers] = useState([
    { days: 7, price: 50000 },
    { days: 15, price: 70000 },
    { days: 30, price: 100000 },
  ]);
  // Precio para la duración actualmente seleccionada (del tarifario admin).
  const currentTier = sponsorTiers.find((t) => t.days === sponsorDays) || sponsorTiers[0];
  const [payConfig, setPayConfig] = useState({ mode: 'simulated', wompi: { enabled: false } });
  const [promoQuota, setPromoQuota] = useState(null);
  const [productUsage, setProductUsage] = useState(null);

  // Porcentaje de comisión REAL vigente para este vendedor, según su plan.
  // Fuente única: el mismo `commission_pct` que usa el backend en el
  // checkout (`server.py` ~línea 2980: `pro_pct if plan=='pro' else basic_pct
  // if plan=='basic' else default_pct`). Si aún no cargó, dejamos null para
  // no mostrar cálculos parciales.
  const [commissionPct, setCommissionPct] = useState(null);
  useEffect(() => {
    const plan = productUsage?.plan || null;
    if (!plan) return;
    api.get('/settings/public').then(({ data }) => {
      const pct = plan === 'pro'
        ? data.pro_plan_commission_pct
        : plan === 'basic'
          ? data.basic_plan_commission_pct
          : data.commission_pct;
      if (typeof pct === 'number' && pct >= 0) setCommissionPct(pct);
    }).catch(() => setCommissionPct(null));
  }, [productUsage?.plan]);

  // Cálculo puro y defensivo del preview. Devuelve null si el precio no es
  // válido — así el UI oculta el bloque en vez de mostrar $NaN o $0.
  // Fórmula idéntica al backend en `POST /orders/checkout`:
  //   commission = round(price * pct / 100, 2)
  //   net        = price - commission
  const commissionPreview = (() => {
    const price = parseFloat(form.price);
    if (!(price > 0) || commissionPct === null) return null;
    const commission = Math.round(price * commissionPct) / 100;
    const net = Math.round((price - commission) * 100) / 100;
    return { price, pct: commissionPct, commission, net };
  })();
  const wompiEnabled = !!payConfig?.wompi?.enabled;

  useSEO({
    title: 'Mis productos | Panel del vendedor',
    description: 'Crea, edita y elimina los repuestos publicados en tu tienda de AutoMarket Colombia.',
    canonical: '/vendedor/productos',
  });

  const fetchData = async () => {
    const { data } = await api.get('/sellers/me/products');
    setItems(data.items || []);
  };

  const fetchQuota = () => {
    api.get('/sellers/me/promo-quota').then(({ data }) => setPromoQuota(data)).catch(() => setPromoQuota(null));
  };

  const fetchUsage = () => {
    api.get('/sellers/me/product-usage').then(({ data }) => setProductUsage(data)).catch(() => setProductUsage(null));
  };

  useEffect(() => {
    fetchData();
    api.get('/categories').then(({ data }) => setCategories(data.categories));
    getPaymentsConfig().then(setPayConfig);
    fetchQuota();
    fetchUsage();
    // Cargar tarifario público de sponsorship (editable desde admin).
    api.get('/sponsorship/pricing').then(({ data }) => {
      const tiers = data.tiers || [];
      if (tiers.length > 0) {
        setSponsorTiers(tiers);
        // Si el días seleccionado por defecto no está en tiers, usar el primero.
        if (!tiers.find((t) => t.days === sponsorDays)) {
          setSponsorDays(tiers[0].days);
        }
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const promoteWithPlan = async (product) => {
    try {
      const { data } = await api.post(`/sellers/products/${product.product_id}/promote-with-plan`);
      toast.success(`Producto promocionado por ${data.days} días (cupo Pro ${data.quota_used}/${data.quota_total})`);
      fetchData();
      fetchQuota();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo promocionar con el plan');
    }
  };

  const paySponsor = async () => {
    if (!sponsorProduct) return;
    setSponsorLoading(true);
    try {
      const method = wompiEnabled ? 'wompi' : 'simulated';
      const { data } = await api.post('/sellers/products/sponsor', {
        product_id: sponsorProduct.product_id, days: sponsorDays, payment_method: method,
      });
      if (method === 'wompi' && data.pending_wompi) {
        await startWompiPayment({
          payment_id: data.payment_id,
          customer_email: user?.email,
          customer_name: user?.name,
          customer_phone: user?.phone,
        });
        return;
      }
      toast.success(`Producto destacado por ${sponsorDays} días (${formatCOP(data.amount)})`);
      setSponsorProduct(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al procesar el pago');
    } finally { setSponsorLoading(false); }
  };

  const save = async () => {
    if (!form.name || !form.price || !form.category) {
      toast.error('Completa nombre, categoría y precio');
      return;
    }
    const payload = {
      ...form,
      price: parseFloat(form.price),
      stock: parseInt(form.stock || 0),
      vehicle_compat: Array.isArray(form.vehicle_compat)
        ? form.vehicle_compat
        : form.vehicle_compat.split(',').map((s) => s.trim()).filter(Boolean),
      images: form.images,
      warranty_months: form.warranty_months ? parseInt(form.warranty_months) : null,
      delivery_days: form.delivery_days ? parseInt(form.delivery_days) : null,
      free_shipping: !!form.free_shipping,
      ...logisticsPayload(form),
      ...shippingPayload(form),
    };
    try {
      if (form.product_id) {
        // Backend PUT expects a full ProductIn — reuse the same payload.
        await api.put(`/products/${form.product_id}`, payload);
        toast.success('Producto actualizado');
      } else {
        await api.post('/products', payload);
        toast.success('Producto creado');
      }
      setOpen(false); setForm(EMPTY); fetchData(); fetchUsage();
    } catch (e) {
      // Bloqueo por límite de plan: mostrar mensaje claro con call-to-action
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && typeof detail === 'object' && detail?.code === 'plan_product_limit_reached') {
        toast.error(detail.message || 'Alcanzaste el límite de productos de tu plan.');
        fetchUsage();
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Error al guardar');
      }
    }
  };

  const startEdit = (p) => {
    setForm({
      product_id: p.product_id,
      name: p.name || '',
      category: p.category || '',
      brand: p.brand || '',
      reference: p.reference || '',
      description: p.description || '',
      price: String(p.price ?? ''),
      stock: String(p.stock ?? ''),
      vehicle_compat: (p.vehicle_compat || []).join(', '),
      images: p.images || [],
      status: p.status || 'active',
      condition: p.condition || 'generico',
      warranty_months: p.warranty_months ? String(p.warranty_months) : '',
      delivery_days: p.delivery_days ? String(p.delivery_days) : '',
      free_shipping: !!p.free_shipping,
      ...logisticsFromProduct(p),
      ...shippingFromProduct(p),
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!confirm('¿Eliminar producto?')) return;
    await api.delete(`/products/${id}`);
    toast.success('Eliminado'); fetchData(); fetchUsage();
  };

  const uploadBulk = async () => {
    if (!file) { toast.error('Selecciona un archivo CSV'); return; }
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post('/products/bulk-upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${data.inserted} productos cargados`);
      if (data.errors?.length) toast.error(`Errores: ${data.errors.length}`);
      setBulkOpen(false); setFile(null); fetchData(); fetchUsage();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && typeof detail === 'object' && detail?.code === 'plan_product_limit_reached') {
        toast.error(detail.message || 'Superarías el límite de productos de tu plan con este CSV.');
        fetchUsage();
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Error');
      }
    }
  };

  const visible = items.filter((p) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [p.name, p.brand, p.category, p.reference].some((x) => (x || '').toLowerCase().includes(s));
  });

  // Bloqueo por plan: cuando el vendedor tiene un plan con límite finito y
  // ya lo llenó, deshabilitamos el botón "Nuevo producto" y mostramos un
  // banner con la CTA de upgrade.
  const limitReached = productUsage && productUsage.limit > 0 && productUsage.used >= productUsage.limit;
  const nearLimit = productUsage && productUsage.limit > 0 && !limitReached && productUsage.remaining <= 5;

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Vendedor</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Mis productos</h1>
            {productUsage && productUsage.limit > 0 && (
              <div
                data-testid="seller-product-usage"
                className={`mt-1 text-xs ${limitReached ? 'text-rose-700' : nearLimit ? 'text-amber-700' : 'text-slate-500'}`}
              >
                {productUsage.used}/{productUsage.limit} productos publicados
                {productUsage.plan && (
                  <span className="text-slate-400"> · plan {productUsage.plan.toUpperCase()}</span>
                )}
              </div>
            )}
            <PackageDataNotice refreshKey={`${items.length}-${open}`}
              onEdit={(pid) => { const p = items.find(x => x.product_id === pid); if (p) startEdit(p); }} />

          </div>
          <div className="flex flex-wrap gap-2">
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
              <DialogTrigger asChild>
                <Button data-testid={SELLER.bulkUploadBtn} variant="outline" className="rounded-none">
                  <Upload className="mr-2 h-4 w-4" /> Carga masiva CSV
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none max-w-lg bg-white">
                <DialogHeader><DialogTitle>Carga masiva por CSV</DialogTitle></DialogHeader>
                <div className="space-y-3 text-slate-900">
                  <p className="text-sm text-slate-600">
                    Columnas: <code className="text-xs">name,category,brand,reference,price,stock,description,vehicle_compat,image_url</code>
                  </p>
                  <p className="text-xs text-slate-500">Usa <code>|</code> para separar varios vehículos compatibles.</p>
                  <Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} className="rounded-none" />
                  <Button onClick={uploadBulk} className="rounded-none bg-slate-900 hover:bg-slate-800 text-white w-full">Subir</Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(EMPTY); }}>
              <DialogTrigger asChild>
                <Button
                  data-testid={SELLER.newProductBtn}
                  className="rounded-none bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed"
                  disabled={limitReached}
                  onClick={(e) => { if (limitReached) { e.preventDefault(); toast.error(`Alcanzaste el límite de ${productUsage.limit} productos del plan ${productUsage.plan.toUpperCase()}.`); } }}
                >
                  <Plus className="mr-2 h-4 w-4" /> Nuevo producto
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{form.product_id ? 'Editar producto' : 'Nuevo producto'}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <Input data-testid={SELLER.productNameInput} placeholder="Nombre" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none col-span-2" />
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid={SELLER.productCategory} className="rounded-none"><SelectValue placeholder="Categoría" /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input data-testid={SELLER.productBrand} placeholder="Marca" value={form.brand}
                    onChange={(e) => setForm({ ...form, brand: e.target.value })} className="rounded-none" />
                  <Input data-testid={SELLER.productReference} placeholder="Referencia" value={form.reference}
                    onChange={(e) => setForm({ ...form, reference: e.target.value })} className="rounded-none" />
                  <Input data-testid={SELLER.productPrice} type="number" placeholder="Precio (COP)" value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-none" />
                  <Input data-testid={SELLER.productStock} type="number" placeholder="Stock" value={form.stock}
                    onChange={(e) => setForm({ ...form, stock: e.target.value })} className="rounded-none" />
                  <Input data-testid={SELLER.productCompat} placeholder="Compatibilidad (sep. coma)" value={form.vehicle_compat}
                    onChange={(e) => setForm({ ...form, vehicle_compat: e.target.value })} className="rounded-none col-span-2" />

                  {/* Preview de comisión y neto en tiempo real. Usa el mismo
                      `commission_pct` que el backend aplica en el checkout:
                      pro=5% / basic=7% / sin plan=8% (según settings). Si el
                      precio no es un número > 0 o aún no cargó el plan, el
                      bloque se oculta para no mostrar cálculos incorrectos. */}
                  {commissionPreview && (
                    <div
                      data-testid="seller-product-commission-preview"
                      className="col-span-2 border border-orange-200 bg-orange-50/60 p-3 space-y-1 text-sm"
                    >
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-700 mb-1">
                        Vista previa del cálculo
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-700">Precio de venta</span>
                        <span data-testid="preview-price" className="font-bold text-slate-900">
                          {formatCOP(commissionPreview.price)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-700">
                          Comisión AutoMarket ({commissionPreview.pct}%)
                        </span>
                        <span data-testid="preview-commission" className="font-bold text-rose-600">
                          −{formatCOP(commissionPreview.commission)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-t border-orange-200 pt-1 mt-1">
                        <span className="text-slate-900 font-bold">Recibirás NETO</span>
                        <span data-testid="preview-net" className="font-display font-black text-emerald-700 text-base">
                          {formatCOP(commissionPreview.net)}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1">
                        El comprador paga {formatCOP(commissionPreview.price)} exactos. El costo de pasarela lo asume AutoMarket.
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Condición</label>
                    <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                      <SelectTrigger data-testid="seller-product-condition" className="rounded-none mt-1">
                        <SelectValue placeholder="Condición" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="generico">Genérico</SelectItem>
                        <SelectItem value="original">Original</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Garantía (meses)</label>
                    <Input
                      data-testid="seller-product-warranty"
                      type="number" min="0" placeholder="Ej: 12"
                      value={form.warranty_months}
                      onChange={(e) => setForm({ ...form, warranty_months: e.target.value })}
                      className="rounded-none mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Días de entrega</label>
                    <Input
                      data-testid="seller-product-delivery"
                      type="number" min="1" placeholder="Ej: 3"
                      value={form.delivery_days}
                      onChange={(e) => setForm({ ...form, delivery_days: e.target.value })}
                      className="rounded-none mt-1"
                    />
                    <ProductShippingType form={form} setForm={setForm} />
                  </div>

                  <ProductLogisticsFields form={form} setForm={setForm} />

                  {/* Image uploader */}
                  <div className="col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                      Imágenes del producto
                    </label>
                    <div className="mt-2">
                      <ImageUploader
                        testId="seller-image-uploader"
                        images={form.images}
                        onChange={(imgs) => setForm({ ...form, images: imgs })}
                      />
                    </div>
                  </div>

                  <Textarea data-testid={SELLER.productDescription} placeholder="Descripción" value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none col-span-2" rows={3} />
                </div>
                <Button data-testid={SELLER.productSaveBtn} onClick={save} className="rounded-none bg-slate-900 hover:bg-slate-800 mt-3 text-white">
                  {form.product_id ? 'Guardar cambios' : 'Guardar producto'}
                </Button>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {(limitReached || nearLimit) && productUsage && (
          <div
            data-testid={limitReached ? 'seller-limit-reached-banner' : 'seller-limit-warning-banner'}
            className={`mt-4 border-2 p-4 flex items-start gap-3 ${
              limitReached ? 'border-rose-300 bg-rose-50' : 'border-amber-300 bg-amber-50'
            }`}
          >
            <div className="flex-1 text-sm">
              <div className={`font-bold ${limitReached ? 'text-rose-800' : 'text-amber-800'}`}>
                {limitReached
                  ? `Alcanzaste el límite del plan ${productUsage.plan.toUpperCase()}`
                  : `Te quedan ${productUsage.remaining} espacios en el plan ${productUsage.plan.toUpperCase()}`}
              </div>
              <div className={`mt-0.5 ${limitReached ? 'text-rose-700' : 'text-amber-700'}`}>
                {productUsage.plan === 'basic'
                  ? 'Actualiza al plan PRO para publicar sin límite y desbloquear beneficios adicionales.'
                  : 'Contacta a soporte para ampliar tu cupo.'}
              </div>
            </div>
            {productUsage.plan === 'basic' && (
              <a
                href="/vendedor/suscripcion"
                data-testid="seller-upgrade-plan-cta"
                className="shrink-0 inline-flex items-center gap-1 bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold text-sm px-4 py-2 rounded"
              >
                Actualizar a PRO
              </a>
            )}
          </div>
        )}

        {/* Search bar */}
        <div className="mt-6 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="seller-products-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, marca, referencia..."
            className="pl-9 rounded-none"
          />
        </div>

        <div className="mt-4 text-sm text-slate-600">{visible.length} productos</div>

        <div className="mt-3 border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Producto</th>
                <th className="px-4 py-3">Categoría</th>
                <th className="px-4 py-3">Marca</th>
                <th className="px-4 py-3">Precio</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Sin productos. Crea el primero o carga masivamente.</td></tr>}
              {visible.map((p) => (
                <tr key={p.product_id} className="row-hover">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.images?.[0] && <img src={resolveImageUrl(p.images[0])} alt="" className="h-10 w-10 object-cover" />}
                      <div>
                        <div className="font-semibold text-slate-900">{p.name}</div>
                        <div className="text-xs text-slate-500 font-mono">{p.reference}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{p.category}</td>
                  <td className="px-4 py-3 text-slate-700">{p.brand}</td>
                  <td className="px-4 py-3 font-bold">{formatCOP(p.price)}</td>
                  <td className="px-4 py-3">{p.stock}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      {promoQuota?.plan === 'pro' && promoQuota?.subscription_status === 'active' && promoQuota?.quota_available > 0 && !p.sponsored_until && (
                        <button
                          data-testid={`seller-product-promote-pro-${p.product_id}`}
                          onClick={() => promoteWithPlan(p)}
                          className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 text-xs font-semibold"
                          title={`Promoción incluida en tu plan Pro (${promoQuota.quota_available} disponibles este mes)`}
                        >
                          <TrendingUp className="h-3.5 w-3.5" /> Promocionar (Pro)
                        </button>
                      )}
                      <button
                        data-testid={`seller-product-sponsor-${p.product_id}`}
                        onClick={() => { setSponsorProduct(p); setSponsorDays(7); }}
                        className="text-amber-700 hover:text-amber-900 inline-flex items-center gap-1 text-xs font-semibold"
                        title={p.sponsored_until ? `Ya destacado hasta ${p.sponsored_until.slice(0,10)}` : 'Destacar en catálogo'}
                      >
                        <TrendingUp className="h-3.5 w-3.5" /> Destacar
                      </button>
                      <button
                        data-testid={`seller-product-edit-${p.product_id}`}
                        onClick={() => startEdit(p)}
                        className="text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 text-xs font-semibold"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Editar
                      </button>
                      <button
                        data-testid={SELLER.productDeleteBtn(p.product_id)}
                        onClick={() => remove(p.product_id)}
                        className="text-rose-700 hover:text-rose-900 inline-flex items-center gap-1 text-xs"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sponsor dialog */}
      <Dialog open={!!sponsorProduct} onOpenChange={(o) => !o && setSponsorProduct(null)}>
        <DialogContent className="rounded-none max-w-md" data-testid="sponsor-dialog">
          <DialogHeader><DialogTitle>Destacar producto</DialogTitle></DialogHeader>
          {sponsorProduct && (
            <div className="space-y-4">
              <div className="text-sm text-slate-700 line-clamp-2">{sponsorProduct.name}</div>
              <div className="flex items-center gap-2 flex-wrap">
                {sponsorTiers.map((t) => (
                  <button
                    key={t.days}
                    data-testid={`sponsor-days-${t.days}`}
                    onClick={() => setSponsorDays(t.days)}
                    className={`px-3 h-14 border-2 font-bold flex flex-col items-center justify-center ${
                      sponsorDays === t.days ? 'border-orange-500 bg-orange-50' : 'border-slate-200'
                    }`}
                  >
                    <span className="text-sm">{t.days} días</span>
                    <span className="text-xs font-normal text-slate-600">{formatCOP(t.price)}</span>
                  </button>
                ))}
              </div>
              <div className="text-xs text-slate-500 border-l-2 border-orange-300 pl-3 space-y-1">
                <div>Costo total: <strong className="text-slate-900">{formatCOP(currentTier?.price || 0)}</strong> por {sponsorDays} días.</div>
                <div>Tu producto tendrá <strong>prioridad de posicionamiento</strong> en el catálogo, la búsqueda, la sección Ofertas y Novedades, con badge de "Promocionado".</div>
                <div>Al vencer el período, la prioridad se retira automáticamente.</div>
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-2">
                {wompiEnabled
                  ? <>Pago seguro con <strong>Wompi</strong> ({payConfig?.wompi?.env})</>
                  : <>Modo <strong>simulado</strong> (MVP)</>}
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setSponsorProduct(null)}
                  className="rounded-none"
                >Cancelar</Button>
                <Button
                  data-testid="sponsor-pay-btn"
                  disabled={sponsorLoading}
                  onClick={paySponsor}
                  className="rounded-none bg-orange-500 hover:bg-orange-400 text-slate-900 font-bold"
                >
                  {sponsorLoading ? 'Procesando…' : (wompiEnabled ? 'Pagar con Wompi' : 'Pagar')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
